import { MomentInput } from "moment";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Booking } from "PFTypes";
import { MutableRefObject, useEffect, useRef } from "react";

import { Mode } from "./index";
import useUpdateBooking from "./use_update_booking";

interface Props {
  booking: Booking;
  draggingMode: MutableRefObject<Mode | null>;
  onMouseUpStart: () => void;
  onMouseUpEnd: (data?: { daysDiff: number }) => void;
  draggingDates: MutableRefObject<{ start: MomentInput; end: MomentInput }>;
  onError: () => void;
}

const useOnMouseUp = ({
  booking,
  draggingMode,
  onMouseUpStart,
  onMouseUpEnd,
  draggingDates,
  onError
}: Props) => {
  const { updateBooking, loading } = useUpdateBooking({ booking, onError });
  const { utc } = useDateFormatter();

  const onMouseUp = (booking: Booking) => (event: Event) => {
    onMouseUpStart();
    event.preventDefault();
    event.stopPropagation();
    if (!booking || !draggingMode.current) {
      onMouseUpEnd();
      return;
    }
    const { start, end } = draggingDates.current;
    const daysDiff = utc(start).diff(booking.start_date, "days") || utc(end).diff(booking.end_date, "days");

    new Promise<void>((resolve) => setTimeout(resolve, 0)) //workaround to not trigger click on bar when dragging to initial position
      .then(() => {
        if (daysDiff !== 0) {
          return updateBooking({
            startDate: start,
            endDate: end
          });
        }
      })
      .finally(() => {
        onMouseUpEnd({ daysDiff });
      });
  };

  const onMouseUpRef = useRef<EventListener>();

  useEffect(() => {
    onMouseUpRef.current = onMouseUp(booking);
  }, [booking]);

  return {
    onMouseUpRef,
    loading
  };
};

export default useOnMouseUp;
